/**
 * AccountabilityRegistryPackPage — the installed "Accountability Registry" Agentic App Pack
 * surface (Agent Ownership Lifecycle). A first-class, left-nav destination (/accountability)
 * that presents the pack's tabs over the ownership backend:
 *   Registry · Outcomes & Actions · Ownership Lifecycle · Certificates · Examiner Report
 *
 * The Registry tab is live (Phase-5 read API: /ownership/registry + /ownership/rollup). The
 * remaining tabs are delivered incrementally by this pack's build slices (each wired to the
 * ownership backend — no fabricated data); until wired they render an honest empty state.
 *
 * Reuses the platform's V2PageShell (header + TabNav), v2 primitives, and the existing pack
 * installation API to reflect provisioned state. Route is module-gated (GOVERNANCE) via
 * RequireModuleRoute + moduleForPath, and role-gated (audit.read) in App.jsx.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { V2PageShell, Pill, Button, Card, Label, EmptyState, CustomerPicker } from '../../components/v2';
import { PlusCircle, Boxes } from 'lucide-react';
import RegistryTab from './RegistryTab';
import OutcomesActionsTab from './OutcomesActionsTab';
import CertificatesTab from './CertificatesTab';
import ExaminerReportTab from './ExaminerReportTab';
import LifecycleTab from './LifecycleTab';
import AskRegistryBar from './AskRegistryBar';
import ConfigTab from './ConfigTab';

const PACK_KEY = 'prove7.accountability-registry';

const BASE_TABS = [
  { id: 'registry', label: 'Registry' },
  { id: 'outcomes', label: 'Outcomes & Actions' },
  { id: 'lifecycle', label: 'Ownership Lifecycle' },
  { id: 'certificates', label: 'Certificates' },
  { id: 'examiner', label: 'Examiner Report' },
];

// A-5 — Accountability settings are visible to Platform Admin (any customer) and Super Admin
// (their own). Hiding the tab is presentation only: AccountabilityConfigController enforces
// the same rule via SecurityContext.canBypassScopeForCustomer, and that is the boundary.
const CONFIG_TAB = { id: 'config', label: 'Config' };
const CONFIG_ROLES = new Set(['Platform Admin', 'Super Admin']);

// Every read on this page is scoped to what the caller is accountable for, and the server resolves
// that from their claims — EXCEPT for a Platform Admin, whose token deliberately carries no
// customer or tenant (§8.3). They name a customer once, here, for the whole page.
//
// Deliberately ONE picker at page level rather than one per tab. Four independent pickers on a
// surface whose whole job is stating who owns what could sit on four different customers at once,
// and the tabs cross-reference each other's numbers.
const NEEDS_CUSTOMER_CHOICE = 'Platform Admin';


export default function AccountabilityRegistryPackPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-linkable tab: /accountability?tab=outcomes lands on Outcomes & Actions (e.g. from the
  // agent Promote-gate remediation). Falls back to Registry for any unknown/absent value.
  // §8.4 — session role first; app_users.role is only a fallback.
  const auth = useAuth() || {};
  const canSeeConfig = CONFIG_ROLES.has(auth.currentRole);
  const mustChooseCustomer = auth.currentRole === NEEDS_CUSTOMER_CHOICE;
  // The chosen customer lives in the URL, exactly like the tab does.
  //
  // It did not, and the consequence was not cosmetic: reload or open a bookmark and a Platform
  // Admin was dropped back on whichever customer sorts first — here Acme Corp, which has no
  // tenants — so the saved link reliably opened a page of zeros describing a customer nobody
  // asked about. Anyone sharing "look at this" sent the other person somewhere else entirely.
  // A page that deep-links which TAB you were on but not which CUSTOMER is only half addressable.
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const urlCustomerId = searchParams.get('customerId');
  const [customerId, setCustomerIdState] = useState(urlCustomerId || null);

  // replace, not push: choosing a customer is refining the current view, not navigating somewhere
  // new, so Back should leave the page rather than walk the operator through every customer they
  // clicked. Same call the tab switch makes.
  const setCustomerId = useCallback((id) => {
    setCustomerIdState(id);
    const next = new URLSearchParams(searchParams);
    if (id) next.set('customerId', id); else next.delete('customerId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!mustChooseCustomer) return undefined;
    let alive = true;
    setCustomersLoading(true);
    (async () => {
      try {
        const res = await api.listCustomers();
        const rows = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        if (!alive) return;
        setCustomers(rows);
        // A customer id from the URL is honoured ONLY if the caller can actually see it. An id
        // that is not in their list is dropped rather than passed to the server: a stale or
        // hand-edited link should land on a usable page, not a wall of 403s. Falling back to the
        // first customer is the same behaviour as arriving with no link at all.
        const fromUrl = rows.some((c) => c.id === urlCustomerId) ? urlCustomerId : null;
        const chosen = fromUrl || (rows.length ? rows[0].id : null);
        setCustomerIdState((cur) => (cur && rows.some((c) => c.id === cur) ? cur : chosen));
        if (!fromUrl && chosen) {
          const next = new URLSearchParams(searchParams);
          next.set('customerId', chosen);
          setSearchParams(next, { replace: true });
        }
      } catch (e) {
        if (alive) setCustomers([]);
      } finally {
        if (alive) setCustomersLoading(false);
      }
    })();
    return () => { alive = false; };
    // urlCustomerId is read once on mount by design — later URL edits come from setCustomerId
    // itself, and depending on it here would restart the customer fetch on every selection.
    //
    // No rule-named disable comment here. This project's ESLint config does not load the
    // react-hooks plugin, so naming that plugin's exhaustive-deps rule in a disable directive is
    // itself a build-breaking error ("Definition for rule ... was not found") — which is exactly
    // how this file broke the frontend image. The same trap is already documented in
    // AgentDetailPageV2, AgentMarketplacePageV2, WorkflowIntelligencePage, DashboardLayoutV2 and
    // components/v2/Modal. Since the rule is not loaded there is no warning to suppress, so the
    // prose above is the whole record. Where a directive IS needed here, the house pattern is a
    // bare `// eslint-disable-next-line` with no rule name (see ConnectorOwnerPicker).
  }, [mustChooseCustomer]);
  const TABS = useMemo(
    () => (canSeeConfig ? [...BASE_TABS, CONFIG_TAB] : BASE_TABS),
    [canSeeConfig],
  );
  // Kept in lock-step with TABS so a deep link to ?tab=config is only honoured for a role
  // that can actually use it — otherwise the URL would select a tab that renders nothing.
  const VALID_TABS = useMemo(() => TABS.map((t) => t.id), [TABS]);
  const initialTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'registry';
  const [activeTab, setActiveTab] = useState(initialTab);
  const onTabChange = (t) => {
    setActiveTab(t);
    const next = new URLSearchParams(searchParams);
    if (t === 'registry') next.delete('tab'); else next.set('tab', t);
    setSearchParams(next, { replace: true });
  };

  // Keep the active tab in lock-step with the URL: a same-page navigation to ?tab=outcomes
  // (from a Lifecycle "Remediate" link or the agent gate remediation) must actually switch tabs.
  // useState only reads the URL once at mount, so this effect handles subsequent changes (cache-bust W145).
  React.useEffect(() => {
    const t = searchParams.get('tab');
    if (VALID_TABS.includes(t) && t !== activeTab) {
      setActiveTab(t);
    }
  }, [searchParams]);

  // Keep-alive tabs — mount each tab on first visit and keep it mounted so a
  // switch never re-mounts (a re-mount refetches and flashes a full-height
  // Skeleton "default screen"). Inactive tabs are hidden, not unmounted; each
  // tab refetches in the background on re-activation so data stays fresh.
  const [mountedTabs, setMountedTabs] = useState(() => new Set([initialTab]));
  useEffect(() => {
    setMountedTabs((prev) => (prev.has(activeTab) ? prev : new Set(prev).add(activeTab)));
  }, [activeTab]);

  // Warm the remaining tabs in the background a beat after first paint so the
  // FIRST switch to any tab shows loaded data — no first-visit skeleton flash.
  // Deferred (not eager) so the initial page load only pays for the active
  // tab's fetch; the rest prefetch in idle time. Once warmed they stay mounted
  // (keep-alive above), so every subsequent switch is instant.
  useEffect(() => {
    const warm = () => setMountedTabs((prev) => {
      if (VALID_TABS.every((t) => prev.has(t))) return prev;
      return new Set(VALID_TABS);
    });
    const ric = typeof window !== 'undefined' ? window.requestIdleCallback : null;
    if (typeof ric === 'function') {
      const handle = ric(warm, { timeout: 1500 });
      return () => { if (window.cancelIdleCallback) window.cancelIdleCallback(handle); };
    }
    const timer = setTimeout(warm, 400);
    return () => clearTimeout(timer);
  }, []);
  const [installed, setInstalled] = useState(null); // null = unknown/loading, true/false once resolved
  const [rubricVersion, setRubricVersion] = useState(null); // P0 — real rubric version (not hardcoded)

  // Reflect provisioned state honestly (reuses the existing pack installation API).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.getAgenticAppPackInstallations();
        const rows = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        const inst = rows.find((i) => i.packKey === PACK_KEY && i.status !== 'archived');
        if (alive) setInstalled(!!inst);
      } catch (e) {
        if (alive) setInstalled(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // P0 — read the real rubric version from the engine so the badge is never hardcoded.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.getMaterialityRubric();
        const info = res && res.data !== undefined ? res.data : res;
        if (alive && info && info.version) setRubricVersion(info.version);
      } catch (_e) { /* badge falls back to an unversioned label */ }
    })();
    return () => { alive = false; };
  }, []);

  const tags = useMemo(() => {
    const installTag = installed === null
      ? null
      : installed
        ? <Pill tone="success" size="sm" dot>Installed App Pack</Pill>
        : <Pill tone="muted" size="sm">Available — provision from Pack Gallery</Pill>;
    return (
      <>
        <Pill tone="brand" size="sm" icon={<Boxes size={12} />}>Agentic App Pack</Pill>
        {installTag}
        <Pill tone="muted" size="sm">{`Materiality rubric${rubricVersion ? ` v${rubricVersion}` : ''}`}</Pill>
      </>
    );
  }, [installed, rubricVersion]);

  const actions = (
    <Button
      variant="primary"
      size="sm"
      iconLeft={<PlusCircle size={15} />}
      onClick={() => navigate('/agents?archetype=let&intent=create')}
    >
      Register system
    </Button>
  );

  return (
    <V2PageShell
      title="Accountability Registry"
      subtitle="Agent Ownership Lifecycle — for the Accountable Human. Who owns what, right now — provably."
      tags={tags}
      actions={actions}
      tabs={TABS}
      activeTabId={activeTab}
      onTabChange={onTabChange}
    >
      {mustChooseCustomer && (
        <Card>
          <Label htmlFor="acct-customer">Customer</Label>
          <CustomerPicker
            customers={customers}
            value={customerId}
            loading={customersLoading}
            onChange={setCustomerId}
            ariaLabel="Choose the customer whose accountability you want to see"
          />
          <div className="text-[12px] text-v2-ink-muted mt-2">
            Your account works across customers, so every figure on this page needs to know which
            one you mean. Everything below covers all of this customer's tenants.
          </div>
        </Card>
      )}
      {/* Do not mount the tabs until the scope is known. Every tab fetches on mount, so rendering
          them before the customer resolves fired four requests with no customerId, each answered
          400 customer_required, and each caught into a null the tabs render as zeroes. The page
          load literally began by showing a Platform Admin a clean estate it had failed to read.
          A role whose scope comes from their claims is ready immediately; only a Platform Admin
          waits, and only for as long as the customer list takes. */}
      {mustChooseCustomer && !customerId ? (
        <Card padding="none">
          <EmptyState
            icon={<Boxes size={22} />}
            title={customersLoading ? 'Loading your customers…' : 'Choose a customer to begin'}
            description={customersLoading
              ? 'Your account works across customers, so this page needs to know which one you mean.'
              : 'Pick a customer above to see who owns what across all of their tenants. Nothing is shown until you choose, because a figure with no customer behind it would not mean anything.'}
          />
        </Card>
      ) : (
      <>
      <AskRegistryBar />
      {[
        ['registry', RegistryTab],
        ['outcomes', OutcomesActionsTab],
        ['lifecycle', LifecycleTab],
        ['certificates', CertificatesTab],
        ['examiner', ExaminerReportTab],
        ...(canSeeConfig ? [['config', ConfigTab]] : []),
      ].map(([id, Tab]) => (
        mountedTabs.has(id) ? (
          <div key={id} hidden={activeTab !== id}>
            <Tab active={activeTab === id} customerId={customerId} />
          </div>
        ) : null
      ))}
      </>
      )}
    </V2PageShell>
  );
}
